import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import FileSaver from "file-saver";
import { DriveListReqeust, DropboxFilesInfo } from "../../types/app";
import appApi, { PRODUCT_ITEM_TYPE } from "../api/appApi";

export interface DriveFolderListDTO {
  id: string;
  name: string;
  subFolders: DriveFolderListDTO[];
}
interface AppState {
  appRegisterStatus: {
    imap: boolean;
    googleDrive: boolean;
    dropbox: boolean;
    zoom: boolean;
  };
  dropboxFile?: DropboxFilesInfo;
  dropboxFolderList?: DriveFolderListDTO;
  currentLoc?: { type?: "googleDrive" | "dropbox"; folder?: string };
  googleDriveFolderList?: DriveFolderListDTO;
  sortBy: "file" | "filedesc" | "date" | "datedesc";
}

// # initial state
const initialState: AppState = {
  appRegisterStatus: {
    imap: false,
    googleDrive: false,
    dropbox: false,
    zoom: false,
  },
  dropboxFile: undefined,
  sortBy: "datedesc",
  currentLoc: { type: undefined, folder: "" },
};

export const trunkGetAppLoginPage = createAsyncThunk(
  "app/register",
  async (snsType: PRODUCT_ITEM_TYPE) => {
    const resp = await appApi.getAppLoginPage(snsType);
    return resp;
  }
);
export const trunkDoAppLogout = createAsyncThunk(
  "app/revoke",
  async (snsType: PRODUCT_ITEM_TYPE) => {
    const resp = await appApi.doAppLogout(snsType);
    return resp;
  }
);
export const trunkGetAppLoginInfo = createAsyncThunk(
  "app/info",
  async (snsType: PRODUCT_ITEM_TYPE) => {
    const resp = await appApi.getAppLoginInfo(snsType);
    return resp;
  }
);
export const trunkGetDropboxList = createAsyncThunk(
  "app/dropbox/files",
  async (req: DriveListReqeust) => {
    const resp = await appApi.getDropboxList(req);
    return resp;
  }
);
export const trunkGetDriveFolderList = createAsyncThunk(
  "app/drive/folders",
  async (req: { type: PRODUCT_ITEM_TYPE; folder: string }) => {
    const resp = await appApi.getDriveFolderList(req);
    return resp;
  }
);
export const trunkUploadDropboxToEco = createAsyncThunk(
  "app/dropbox/files/uploadToEco",
  async (req: {
    uid: string;
    fileId: string;
    setProgressPercent?: any;
    name: string;
  }) => {
    const resp = await appApi.uploadDropboxToEco(req);
    return resp;
  }
);
export const trunkUploadEcoToDrive = createAsyncThunk(
  "app/dropbox/files/uploadFromEco",
  async (req: {
    type: "google" | "dropbox";
    syncId: string;
    revisionNo: number;
    targetFolder?: string;
  }) => {
    const resp = await appApi.uploadEcoToDrive(req);
    return resp;
  }
);
export const trunkChangeDriveSort = createAsyncThunk(
  "app/drive/sort",
  async (sort: "file" | "filedesc" | "date" | "datedesc") => {
    return sort;
  }
);
const removeDuplicatesByUid = (list: any[]): any[] => {
  return Object.values(
    list.reduce<Record<string, any>>((acc, item) => {
      acc[item.id] = item; // msgUid 기준으로 덮어쓰기
      return acc;
    }, {})
  );
};
const appSlice = createSlice({
  name: "app",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(trunkGetAppLoginInfo.fulfilled, (state, arg) => {
      switch (arg.meta.arg) {
        case "googleDrive":
          state.appRegisterStatus.googleDrive = true;
          break;
        case "dropbox":
          state.appRegisterStatus.dropbox = true;
          break;
        case "zoom":
          state.appRegisterStatus.zoom = true;
          break;
      }
    });
    builder.addCase(trunkGetAppLoginInfo.rejected, (state, arg) => {
      switch (arg.meta.arg) {
        case "googleDrive":
          state.appRegisterStatus.googleDrive = false;
          break;
        case "dropbox":
          state.appRegisterStatus.dropbox = false;
          break;
        case "zoom":
          state.appRegisterStatus.zoom = false;
          break;
      }
    });
    builder.addCase(trunkDoAppLogout.fulfilled, (state, arg) => {
      switch (arg.meta.arg) {
        case "googleDrive":
          state.appRegisterStatus.googleDrive = false;
          break;
        case "dropbox":
          state.appRegisterStatus.dropbox = false;
          break;
        case "zoom":
          state.appRegisterStatus.zoom = false;
          break;
      }
    });
    builder.addCase(trunkGetDropboxList.fulfilled, (state, res) => {
      const { isRefresh, pageToken, folderPath } = res.meta.arg;
      state.currentLoc = {
        type: "dropbox",
        folder: folderPath === "/" ? "" : folderPath,
      };
      const newFiles = removeDuplicatesByUid([
        ...(state.dropboxFile?.files ?? []),
        ...(res.payload.entity.files ?? []),
      ]);

      state.dropboxFile = {
        ...res.payload.entity,
        files:
          isRefresh || !pageToken ? res.payload.entity.files ?? [] : newFiles,
        nextPageToken: isRefresh
          ? state.dropboxFile?.nextPageToken ?? ""
          : res.payload.entity.nextPageToken,
      };
    });
    builder.addCase(
      trunkChangeDriveSort.fulfilled,
      (state, { payload: pay }) => {
        state.sortBy = pay;
      }
    );
    builder.addCase(trunkUploadEcoToDrive.fulfilled, (state, res) => {
      if (
        state.currentLoc?.type !== "dropbox" ||
        state.currentLoc.folder !== res.meta.arg.targetFolder
      ) {
        console.log("I cannot save you", res.meta.arg);
        return;
      } else {
        console.log("I can save you");
      }

      const targetFilesList = state.dropboxFile?.files.filter(
        (item) => !!item.size
      );
      const nextToken = state.dropboxFile?.nextPageToken;
      let requiredPush = false;
      if (!nextToken || !targetFilesList) {
        requiredPush = true;
      } else {
        if (
          state.sortBy === "date" &&
          targetFilesList[targetFilesList.length - 1].serverModified >
            res.payload.entity.date
        ) {
          requiredPush = true;
        }
        if (state.sortBy === "datedesc") {
          requiredPush = true;
        }
        if (
          state.sortBy === "file" &&
          targetFilesList[targetFilesList.length - 1].name >=
            res.payload.entity.name
        ) {
          requiredPush = true;
        }
        if (
          state.sortBy === "filedesc" &&
          targetFilesList[targetFilesList.length - 1].name <=
            res.payload.entity.name
        ) {
          requiredPush = true;
        }
      }
      if (requiredPush && state.dropboxFile) {
        state.dropboxFile = {
          ...state.dropboxFile,
          files: [
            ...(state.dropboxFile && state.dropboxFile.files
              ? state.dropboxFile.files
              : []),
            {
              serverModified: res.payload.entity.date,
              id: res.payload.entity.fileId,
              size: res.payload.entity.size,
              name: res.payload.entity.name,
              pathLower: "",
              pathDisplay: "",
              parentSharedFolderId: null,
              previewUrl: null,
              clientModified: 0,
              rev: "",
              mediaInfo: null,
              symlinkInfo: null,
              sharingInfo: null,
              isDownloadable: false,
              exportInfo: null,
              propertyGroups: null,
              hasExplicitSharedMembers: null,
              contentHash: "",
              fileLockInfo: null,
            },
          ],
        };
      }
    });
  },
});
export const appActions = appSlice.actions;

export default appSlice.reducer;
